import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, User, Phone, Shield, RefreshCw } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import api from '../api';
import { setTokenExpiry } from '../utils/auth';

axiosRetry(axios, { retries: 3, retryDelay: (retryCount) => retryCount * 1000 });

const loginSchema = z.object({
  cardNumber: z.string().min(4, 'Card number must be 4 digits').max(4, 'Card number must be 4 digits').regex(/^\d+$/, 'Only numbers allowed'),
  cnic: z.string().min(13, 'CNIC must be 13 digits').max(13, 'CNIC must be 13 digits').regex(/^\d+$/, 'Only numbers allowed'),
  mobile: z.string().min(11, 'Mobile number must be 11 digits').max(11, 'Mobile number must be 11 digits').regex(/^\d+$/, 'Only numbers allowed'),
  captcha: z.string().min(1, 'Captcha is required'),
});

const config = {
  labels: {
    cardNumber: 'Card Number (Last 4 Digits)',
    cnic: 'CNIC Number',
    mobile: 'Mobile Number',
    captcha: 'Verification',
    submit: 'Login',
    loading: 'Logging in...',
    title: 'Your Card, Your Control. Log In and Take Charge',
    subtitle: 'Access your account securely',
    securityDisclaimer: 'For your security, please ensure you are on a secure network and never share your credentials with anyone.',
  },
  apiUrl: process.env.REACT_APP_API_URL,
  bgImage: process.env.REACT_APP_BG_IMAGE || '/images/login-bg.jpg',
};

const Home = () => {
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const captchaUrlRef = useRef(null);

  const { register, handleSubmit, formState: { errors }, reset, setFocus } = useForm({
    resolver: zodResolver(loginSchema),
  });

  useEffect(() => {
    loadCaptcha();
    setFocus('cardNumber');
  }, [captchaKey]);

  useEffect(() => {
    return () => {
      if (captchaUrlRef.current) URL.revokeObjectURL(captchaUrlRef.current);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const otpVerified = localStorage.getItem('otpVerified') === 'true';

    if (token && otpVerified) {
      navigate('/main', { replace: true });
      return;
    }

    if (token && !otpVerified) {
      navigate('/otp', { replace: true });
    }
  }, [navigate]);

  const loadCaptcha = () => {
    if (captchaUrlRef.current) URL.revokeObjectURL(captchaUrlRef.current);
    axios.get(`${config.apiUrl}/api/auth/captcha`, {
      responseType: 'blob',
      withCredentials: true,
    })
      .then(response => {
        const url = URL.createObjectURL(response.data);
        captchaUrlRef.current = url;
        setCaptchaImage(url);
      })
      .catch(() => {
        toast.error('Failed to load CAPTCHA');
      });
  };

  const refreshCaptcha = () => {
    setCaptchaKey(prev => prev + 1);
    loadCaptcha();
  };

  const onSubmit = async (data) => {
  setIsLoading(true);
  try {
    const response = await api.post(`${config.apiUrl}/api/auth/login`, {
      cardNumber: btoa(data.cardNumber),
      cnic: btoa(data.cnic),
      mobile: btoa(data.mobile),
      captchaToken: data.captcha,
    }, {
      withCredentials: true,
    });

    // 1. Destructure correctly from response.data
    const { accessToken, refreshToken, redirect } = response.data;

    // 2. Store tokens
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('cnic', data.cnic);
    localStorage.setItem('otpVerified', 'false');
    localStorage.setItem('postOtpRedirect',
      redirect && redirect.toLowerCase() === 'nocotp' ? '/noc' : '/main'
    );
    localStorage.setItem('showDashboardToast', 'true');
    setTokenExpiry();

    setIsLoading(false);
    toast.success('OTP sent successfully! Please enter it to continue.');
    reset();
    refreshCaptcha();

    navigate('/otp', { replace: true });

  } catch (err) {
    setIsLoading(false);
    const errorMsg = err.response?.data?.message || 'Login failed';
    toast.error(errorMsg.includes('CAPTCHA') ? 'Invalid CAPTCHA. Please try again.' : errorMsg);
    reset();
    refreshCaptcha();
  }
};


  return (
    <div
      className="min-h-[100vh] w-full flex items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url('${config.bgImage}')` }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-sm mx-4"
      >
        <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl p-6 border border-white/20">
          <div className="text-center mb-6">
            <div className="bg-gradient-to-br from-green-600 to-green-700 p-2 rounded-2xl shadow-lg w-fit mx-auto mb-3">
              <CreditCard className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 font-['Inter'] bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent leading-tight">
              {config.labels.title.split(',').map((line, idx) => (
                <span key={idx}>
                  {line.trim()}
                  <br />
                </span>
              ))}
            </h2>
            <p className="text-slate-600 mt-1 text-sm">{config.labels.subtitle}</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{config.labels.cardNumber}</label>
              <div className="relative">
                <input
                  {...register('cardNumber')}
                  type="text"
                  placeholder="xxxx"
                  className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-xl bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 text-sm h-10 leading-10"
                  maxLength={4}
                  aria-describedby="cardNumber-error"
                />
                <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              </div>
              {errors.cardNumber && (
                <p id="cardNumber-error" className="text-red-500 text-xs mt-1">{errors.cardNumber.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{config.labels.cnic}</label>
              <div className="relative">
                <input
                  {...register('cnic')}
                  type="text"
                  placeholder="xxxxxxxxxxxxx"
                  className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-xl bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 text-sm h-10 leading-10"
                  maxLength={13}
                  aria-describedby="cnic-error"
                />
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              </div>
              {errors.cnic && (
                <p id="cnic-error" className="text-red-500 text-xs mt-1">{errors.cnic.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{config.labels.mobile}</label>
              <div className="relative">
                <input
                  {...register('mobile')}
                  type="text"
                  placeholder="03xxxxxxxxx"
                  className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-xl bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 text-sm h-10 leading-10"
                  maxLength={11}
                  aria-describedby="mobile-error"
                />
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              </div>
              {errors.mobile && (
                <p id="mobile-error" className="text-red-500 text-xs mt-1">{errors.mobile.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{config.labels.captcha}</label>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  {captchaImage && <img key={captchaKey} src={captchaImage} alt="Verification Code" className="h-8 rounded border" />}
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full h-5 w-5 flex items-center justify-center hover:bg-red-600"
                    aria-label="Refresh Verification"
                  >
                    <RefreshCw className="h-3 w-3" />
                  </button>
                </div>
                <div className="relative flex-1">
                  <input
                    {...register('captcha')}
                    type="text"
                    placeholder="Enter Verification Code"
                    className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-xl bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 text-sm h-10 leading-10"
                    aria-describedby="captcha-error"
                  />
                  <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                </div>
              </div>
              {errors.captcha && (
                <p id="captcha-error" className="text-red-500 text-xs mt-1">{errors.captcha.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              aria-disabled={isLoading}
              className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-2 px-4 rounded-xl font-medium hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl text-sm"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true"></div>
                  <span aria-live="polite">{config.labels.loading}</span>
                </div>
              ) : (
                config.labels.submit
              )}
            </button>
            
            {config.labels.securityDisclaimer && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800 text-center">
                  <Shield className="inline h-3 w-3 mr-1" />
                  {config.labels.securityDisclaimer}
                </p>
              </div>
            )}
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default Home;